import { CSSProperties, HTMLProps, ReactNode, useId } from 'react';
import styled, { css } from 'styled-components';

import {
  U21Spacer,
  U21SpacerAlign,
} from 'app/shared/u21-ui/components/layout/U21Spacer';
import {
  U21Typography,
  U21TypographyProps,
} from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21NoValue } from 'app/shared/u21-ui/components/display/U21NoValue';
import { getDOMProps, isRenderable } from 'app/shared/utils/react';
import { U21CopyButton } from 'app/shared/u21-ui/components/display/U21CopyButton';

export interface U21InfoItemProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'label' | 'ref' | 'value'
> {
  children: ReactNode;
  enableCopy?: boolean;
  fullWidth?: boolean;
  hideWhenEmpty?: boolean;
  label: ReactNode;
  labelProps?: Omit<U21TypographyProps, 'as' | 'children'>;
  /** Vertical alignment of label vs value in the horizontal row (e.g. center for tall controls). */
  rowAlign?: U21SpacerAlign;
  valueProps?: Omit<U21TypographyProps, 'as' | 'children'>;
  width?: CSSProperties['width'];
}

export const U21InfoItem = (props: U21InfoItemProps) => {
  const {
    children,
    enableCopy,
    fullWidth,
    hideWhenEmpty,
    label,
    labelProps,
    rowAlign = 'start',
    valueProps,
    width = '25%',
    ...rest
  } = props;
  const labelContainerId = useId();

  if (hideWhenEmpty && !isRenderable(children)) {
    return null;
  }

  const { 'aria-describedby': ariaDescribedBy } = valueProps ?? {};
  const a11yValueProps = {
    'aria-describedby': `${ariaDescribedBy ?? ''} ${labelContainerId}`,
  };

  return (
    <U21Spacer
      align={rowAlign}
      horizontal
      spacing={0.5}
      useFlexGap
      {...getDOMProps(rest)}
    >
      <LabelContainer $width={width} id={labelContainerId}>
        {typeof label === 'string' || typeof label === 'number' ? (
          <U21Typography
            color="text.secondary"
            ellipsis
            variant="body2"
            {...labelProps}
          >
            {label}
          </U21Typography>
        ) : (
          label
        )}
      </LabelContainer>
      {(() => {
        if (!isRenderable(children)) {
          return <U21NoValue {...valueProps} {...a11yValueProps} />;
        }
        if (typeof children === 'string' || typeof children === 'number') {
          return (
            <U21Typography
              ellipsis
              variant="body2"
              {...valueProps}
              {...a11yValueProps}
            >
              {children}
            </U21Typography>
          );
        }
        return (
          <ValueContainer $fullWidth={fullWidth} {...a11yValueProps}>
            {children}
          </ValueContainer>
        );
      })()}
      {enableCopy &&
        (typeof children === 'string' || typeof children === 'number') && (
          <StyledU21CopyButton text={String(children)} />
        )}
    </U21Spacer>
  );
};

const LabelContainer = styled.div<{
  $width: CSSProperties['width'];
}>`
  min-width: ${(props) => props.$width};
  ${(props) => {
    if (!props.$width) {
      return css``;
    }
    const width =
      typeof props.$width === 'string' ? props.$width : `${props.$width}px`;

    return css`
      min-width: ${width};
      width: ${width};
    `;
  }}
`;

const ValueContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${(props) =>
    props.$fullWidth
      ? css`
          flex: 1;
        `
      : css``}
`;

const StyledU21CopyButton = styled(U21CopyButton)`
  // align vertically with text
  margin-top: -4px;
`;
